import React, { useState, useMemo, useEffect } from 'react';
import useApi from '../hooks/useApi';
import { Landmark, PlusCircle, MoreVertical, Trash2, Edit } from 'lucide-react';

const AccountCard = ({ account, onDelete, onEdit, formatCurrency }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    
    // Encontra o campo de saldo principal
    const mainBalanceField = account.fields.find(f => f.debits || f.credits || f.rec_pg_fatura);
    const balance = mainBalanceField ? parseFloat(account.customFields[mainBalanceField.name] || 0) : 0;
    
    // Filtra os outros campos para exibição secundária
    const otherFields = account.fields.filter(f => f.name !== mainBalanceField?.name);
    
    return (
        <div className="p-4 bg-white rounded-lg shadow">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-full">
                        <Landmark className="text-blue-600"/>
                    </div>
                    <div>
                        <p className={`font-semibold text-gray-800 ${account.status === 'inativo' ? 'line-through text-gray-400' : ''}`}>
                            {account.name}
                            <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{account.currency}</span>
                        </p>
                        <p className="text-sm text-gray-500">{account.type}</p>
                        {account.status === 'inativo' && <span className="text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">Inativo</span>}
                    </div>
                </div>
                <div className="relative">
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} onBlur={() => setTimeout(() => setIsMenuOpen(false), 200)} className="p-2 rounded-full hover:bg-gray-100">
                        <MoreVertical size={20} />
                    </button>
                    {isMenuOpen && (
                        <div className="absolute right-0 z-10 w-40 mt-2 bg-white rounded-md shadow-lg">
                            <button onClick={() => onEdit(account)} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100">
                                <Edit size={14} /> Editar
                            </button>
                            <button onClick={() => onDelete(account.id)} className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100">
                                <Trash2 size={14} /> Excluir
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {/* Saldo Principal */}
            <div className="mt-4">
                <p className="text-sm text-gray-600">{mainBalanceField ? mainBalanceField.name : 'Saldo'}</p>
                <p className="text-2xl font-bold text-gray-800">
                    {formatCurrency(balance, account.currency)}
                </p>
            </div>
            {/* Outros Campos */}
            {otherFields.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 pt-4 border-t border-gray-100">
                    {otherFields.map(field => {
                        const value = account.customFields[field.name];
                        const isCurrency = field.debits || field.credits || field.rec_pg_fatura;
                        return (
                            <div key={field.name}>
                                <p className="text-xs text-gray-500">{field.name}</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    {isCurrency ? formatCurrency(parseFloat(value) || 0, account.currency) : (value || 'N/A')}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const AccountSummary = ({ accounts, accountTypes, formatCurrency, selectedCurrency }) => {
    const summary = useMemo(() => {
        const typeMap = new Map(accountTypes.map(type => [type.id, { ...type, total: 0 }]));
        
        accounts.forEach(account => {
            const type = typeMap.get(account.account_type_id);
            if (type) {
                const mainField = type.fields.find(f => f.debits || f.credits || f.rec_pg_fatura);
                if (mainField) {
                    const value = parseFloat(account.customFields[mainField.name] || 0);
                    type.total += value;
                }
            }
        });
        
        return Array.from(typeMap.values()).filter(type => type.total !== 0);
    }, [accounts, accountTypes]);

    return (
        <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="mb-4 text-lg font-semibold text-gray-700">Resumo por Tipo ({selectedCurrency})</h3>
            <div className="space-y-3">
                {summary.length > 0 ? summary.map(type => (
                    <div key={type.id} className="flex justify-between text-sm">
                        <span className="text-gray-600">{type.name}</span>
                        <span className="font-semibold">{formatCurrency(type.total, selectedCurrency)}</span>
                    </div>
                )) : (
                    <p className="text-sm text-center text-gray-500">Nenhum saldo a ser exibido para esta moeda.</p>
                )}
            </div>
        </div>
    );
};

const AccountForm = ({ accountToEdit, accountTypes, onFinish, onCancelEdit, allCurrencies, currentUser }) => {
    const isEditing = !!accountToEdit;
    const { apiFetch } = useApi();
    const [name, setName] = useState('');
    const [accountTypeId, setAccountTypeId] = useState('');
    const [customFields, setCustomFields] = useState({});
    const [status, setStatus] = useState('ativo');
    const [currency, setCurrency] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isEditing) {
            setName(accountToEdit.name);
            setAccountTypeId(accountToEdit.account_type_id);
            setCustomFields(accountToEdit.customFields || {});
            setStatus(accountToEdit.status);
            setCurrency(accountToEdit.currency);
        } else {
            setName('');
            setStatus('ativo');
            setCustomFields({});
            if (accountTypes.length > 0) setAccountTypeId(accountTypes[0].id);
            if (currentUser && currentUser.defaultCurrency) {
                setCurrency(currentUser.defaultCurrency);
            } else if (allCurrencies.length > 0) {
                setCurrency(allCurrencies[0].code);
            }
        }
    }, [accountToEdit, isEditing, accountTypes, allCurrencies, currentUser]);
    
    const selectedTypeFields = useMemo(() => {
        const type = accountTypes.find(t => t.id === Number(accountTypeId));
        return type ? type.fields : [];
    }, [accountTypeId, accountTypes]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        const payload = { 
            name, 
            accountTypeId: Number(accountTypeId), 
            customFields, 
            status, 
            currency
        };

        const url = isEditing ? `/accounts/${accountToEdit.id}` : '/accounts';
        const method = isEditing ? 'PUT' : 'POST';
        
        try {
            await apiFetch(url, { method, body: payload });
            onFinish();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="p-6 bg-white rounded-lg shadow sticky top-8">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">{isEditing ? 'Editar Conta' : 'Nova Conta'}</h2>
                {isEditing && <button onClick={onCancelEdit} className="text-sm text-blue-600 hover:underline">Cancelar</button>}
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && <p className="p-2 text-sm text-red-700 bg-red-100 rounded">{error}</p>}
                <div>
                    <label className="text-sm font-medium">Nome da Conta</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} required className="w-full p-2 mt-1 border rounded" />
                </div>
                 <div>
                    <label className="text-sm font-medium">Moeda</label>
                    <select
                        value={currency}
                        onChange={e => setCurrency(e.target.value)}
                        required
                        className={`w-full p-2 mt-1 border rounded ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={isEditing}
                        title={isEditing ? "Não é possível alterar a moeda de uma conta existente." : ""}
                    >
                        {allCurrencies.map(c => <option key={c.code} value={c.code}>{c.symbol} - {c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-sm font-medium">Tipo de Conta</label>
                    <select value={accountTypeId} onChange={e => setAccountTypeId(e.target.value)} required className="w-full p-2 mt-1 border rounded">
                        {accountTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                    </select>
                </div>
                {selectedTypeFields.map(field => (
                    <div key={field.name}>
                        <label className="text-sm font-medium">{field.name}</label>
                        <input
                            type="text"
                            value={customFields[field.name] || ''}
                            onChange={e => setCustomFields({...customFields, [field.name]: e.target.value})}
                            className="w-full p-2 mt-1 border rounded"
                        />
                    </div>
                ))}
                {isEditing && (
                    <div>
                        <label className="text-sm font-medium">Status</label>
                        <select value={status} onChange={e => setStatus(e.target.value)} className="w-full p-2 mt-1 border rounded">
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>
                )}
                <button type="submit" disabled={loading} className="flex items-center justify-center w-full px-4 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                    <PlusCircle className="mr-2" size={20} />
                    {loading ? 'A guardar...' : (isEditing ? 'Guardar Alterações' : 'Adicionar Conta')}
                </button>
            </form>
        </div>
    );
};

const AccountsPage = ({ accounts, accountTypes, onDataChange, selectedCurrency, formatCurrency, allCurrencies, currentUser }) => {
    const [editingAccount, setEditingAccount] = useState(null);
    const [key, setKey] = useState(0); 
    const { apiFetch } = useApi();

    const filteredAccounts = useMemo(() => {
        return accounts.filter(acc => acc.currency === selectedCurrency);
    }, [accounts, selectedCurrency]);

    const handleDelete = async (accountId) => {
        if (window.confirm('Tem a certeza que deseja excluir esta conta? Esta ação não pode ser desfeita.')) {
            try {
                await apiFetch(`/accounts/${accountId}`, { method: 'DELETE' });
                if(onDataChange) onDataChange();
            } catch (error) {
                alert(`Erro ao excluir conta: ${error.message}`);
                if(onDataChange) onDataChange();
            }
        }
    };

    const handleEdit = (account) => {
        setEditingAccount(account);
        setKey(prev => prev + 1); 
    };

    const handleFinish = () => {
        setEditingAccount(null);
        setKey(prev => prev + 1);
        if (onDataChange) onDataChange(); 
    };

    return (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
                <h1 className="text-3xl font-bold text-gray-800">As suas Contas</h1>
                <p className="mb-6 text-gray-600">Visualize e gira as suas contas financeiras para a moeda <span className="font-bold">{selectedCurrency}</span>.</p>
                <div className="space-y-4">
                    {filteredAccounts.length > 0 ? filteredAccounts.map(account => (
                        <AccountCard key={account.id} account={account} onDelete={handleDelete} onEdit={handleEdit} formatCurrency={formatCurrency}/>
                    )) : (
                        <p className="p-4 text-center bg-white rounded-lg shadow text-gray-500">Ainda não adicionou nenhuma conta para a moeda {selectedCurrency}.</p>
                    )}
                </div>
            </div>
            <div>
                 <AccountSummary accounts={filteredAccounts} accountTypes={accountTypes} formatCurrency={formatCurrency} selectedCurrency={selectedCurrency} />
                 <div className="mt-8">
                    <AccountForm 
                        key={key} 
                        accountToEdit={editingAccount} 
                        accountTypes={accountTypes} 
                        onFinish={handleFinish} 
                        onCancelEdit={() => { setEditingAccount(null); setKey(prev => prev + 1); }}
                        allCurrencies={allCurrencies}
                        currentUser={currentUser}
                    />
                </div>
            </div>
        </div>
    );
};

export default AccountsPage;
